import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, CheckCircle, Sparkle, Spinner } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import usePlatform from "../hooks/usePlatform";
import { getAdSessionId } from "../lib/adAttribution";
import { createRazorpayCheckout } from "../lib/razorpayOptions";
import PayPalTopUpButton from "./PayPalTopUpButton";

const RZP_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RZP_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RZP_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/**
 * Wallet top-up modal — Razorpay checkout.
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {number} [props.suggested]      optional pre-selected amount
 * @param {string} [props.headline]       custom headline (e.g. shown after a 402 error)
 * @param {string} [props.subline]
 * @param {() => void} [props.onSuccess]  fires after wallet is credited
 */
export default function WalletTopUp({ open, onClose, suggested, headline, subline, onSuccess, prefilledCoupon }) {
  const { user, checkAuth } = useAuth();
  const { formatPrice, showingUsd, usd_to_inr } = useCurrency();
  const { hidePurchases } = usePlatform();
  const [packs, setPacks] = useState([]);
  const [min, setMin] = useState(50);
  const [max, setMax] = useState(50000);
  const [selected, setSelected] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [paying, setPaying] = useState(false);
  // Coupon state
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {code, bonus, description}
  const [couponChecking, setCouponChecking] = useState(false);
  const [couponError, setCouponError] = useState(null);
  // Opt-in toggle to save the phone number entered on Razorpay's checkout
  // for pre-fill on subsequent top-ups. Defaults to ON — the user only
  // has to type their phone once. If they already have a saved phone,
  // the toggle is disabled since there's nothing new to save.
  const [savePhoneForNext, setSavePhoneForNext] = useState(true);
  const alreadyHasPhone = !!(user?.phone && String(user.phone).trim());

  useEffect(() => {
    if (!open) return;
    api.get("/wallet/packs").then(({ data }) => {
      setPacks(data.packs || []);
      setMin(data.min);
      setMax(data.max);
      // Default selection: nearest pack ≥ suggested, else first pack
      if (suggested) {
        const found = (data.packs || []).find((p) => p.amount >= suggested);
        setSelected(found || (data.packs || [])[1] || null);
      } else {
        setSelected((data.packs || [])[1] || (data.packs || [])[0] || null);
      }
    }).catch(() => toast.error("Couldn't load top-up options"));
  }, [open, suggested]);

  const effectiveAmount = (() => {
    if (customAmount) {
      const n = parseFloat(customAmount);
      if (isNaN(n)) return 0;
      // When the user is on the USD-display path, the number they type is
      // in USD ("5" means $5). Convert to INR (canonical wallet currency)
      // before any backend / coupon math runs.
      if (showingUsd && usd_to_inr) return Math.round(n * usd_to_inr);
      return n;
    }
    return selected?.amount || 0;
  })();
  // Min/max bounds for the placeholder string — same conversion logic so a
  // US visitor sees "$0.50 - $500" rather than "₹50 - ₹50000".
  const displayMin = showingUsd && usd_to_inr ? (min / usd_to_inr).toFixed(2) : min;
  const displayMax = showingUsd && usd_to_inr ? (max / usd_to_inr).toFixed(0) : max;
  const currencySymbol = showingUsd ? "$" : "₹";
  const isValid = effectiveAmount >= min && effectiveAmount <= max;
  const selectedPackBonus = selected && !customAmount ? selected.bonus : 0;
  // GST / Taxes — applied on TOP of the displayed recharge amount.
  // 18% for INR (label "GST"), same rate for USD (label "Taxes").
  const GST_RATE = 0.18;
  const gstAmount = Math.round(effectiveAmount * GST_RATE * 100) / 100;
  const totalToPay = Math.round((effectiveAmount + gstAmount) * 100) / 100;
  const taxLabel = showingUsd ? "Taxes (18%)" : "GST (18%)";
  // First-chat cashback bonus auto-applies on the next successful Razorpay
  // recharge. Cap = min(₹50, eligible amount, recharge amount in INR).
  // Showing it inside the summary so the seeker sees +₹X BEFORE they pay.
  const cashbackEligible = !!user?.first_chat_cashback_eligible;
  const cashbackCap = Number(user?.first_chat_cashback_cap || 50);
  const cashbackAmount = Number(user?.first_chat_cashback_amount || 0);
  // Bonus only credits for INR orders (USD checkouts skip it server-side).
  const cashbackBonusPreview = cashbackEligible && !showingUsd
    ? Math.round(Math.min(cashbackCap, cashbackAmount, effectiveAmount))
    : 0;
  const totalCredit = effectiveAmount + (selectedPackBonus || 0) + (appliedCoupon?.bonus || 0) + cashbackBonusPreview;

  // Re-validate coupon whenever amount changes — must also UPDATE the
  // bonus value when valid (not just nullify on invalid). Also re-fires
  // whenever `appliedCoupon?.code` transitions from null → applied, so
  // that the auto-applied coupon's bonus is reconciled against the
  // *latest* effectiveAmount even when the modal opened with stale
  // pack state (race condition surfaced in iteration_27).
  useEffect(() => {
    if (!appliedCoupon || !effectiveAmount) return;
    let cancelled = false;
    api.post("/wallet/coupon/validate", { code: appliedCoupon.code, amount: effectiveAmount })
      .then(({ data }) => {
        if (cancelled) return;
        if (!data.valid) {
          setAppliedCoupon(null);
          setCouponError(data.error);
          toast(data.error, { icon: "⚠️" });
        } else if (data.bonus !== appliedCoupon.bonus) {
          // Bonus changed (e.g. percent_bonus coupon at a new amount, or
          // crossed the cap threshold). Keep the same code but refresh
          // the displayed bonus so the summary reflects reality.
          setAppliedCoupon({ code: data.code, bonus: data.bonus, description: data.description });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [effectiveAmount, appliedCoupon?.code]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-apply a coupon when the parent supplies `prefilledCoupon` — used
  // by the Anniversary chat banner so tapping "Claim Now" lands the
  // seeker on the topup modal with ANNIVERSARY50 already validated.
  // We use a safe fallback amount (suggested → 500) when effectiveAmount
  // hasn't settled yet on first render, then the OTHER re-validation
  // watcher above keeps the bonus in sync as the user picks a pack /
  // edits the custom amount. This avoids a known race where the modal
  // mounts before the default-pack auto-select effect fires, leaving
  // effectiveAmount at 0 and the prefill silently skipped.
  useEffect(() => {
    if (!open || !prefilledCoupon || appliedCoupon) return;
    const validateAmount = effectiveAmount || suggested || 500;
    setCouponInput(prefilledCoupon);
    api.post("/wallet/coupon/validate", { code: prefilledCoupon, amount: validateAmount })
      .then(({ data }) => {
        if (data?.valid) {
          setAppliedCoupon({ code: data.code, bonus: data.bonus, description: data.description });
        }
      })
      .catch(() => { /* silent — banner CTA shouldn't error-toast on load */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefilledCoupon]);

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    if (!isValid) {
      setCouponError("Pick a recharge amount first.");
      return;
    }
    setCouponChecking(true);
    setCouponError(null);
    try {
      const { data } = await api.post("/wallet/coupon/validate", { code, amount: effectiveAmount });
      if (data.valid) {
        setAppliedCoupon({ code: data.code, bonus: data.bonus, description: data.description });
        setCouponError(null);
        toast.success(`Coupon ${data.code} applied! ₹${data.bonus} extra credit added.`);
      } else {
        setAppliedCoupon(null);
        setCouponError(data.error || "Invalid coupon");
      }
    } catch (err) {
      setCouponError(err?.response?.data?.detail || "Couldn't validate coupon");
    } finally {
      setCouponChecking(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponError(null);
  };

  const handlePay = async () => {
    if (!isValid || paying) return;
    setPaying(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded || !window.Razorpay) {
        toast.error("Could not load Razorpay. Check your connection.");
        return;
      }
      const { data: order } = await api.post("/wallet/razorpay/order", {
        amount: effectiveAmount,
        coupon_code: appliedCoupon?.code || null,
        // Tell the backend to create a USD order when the user is on the
        // USD-display path (US/UK/etc visitors). For INR visitors this
        // stays "INR" — backend defaults to INR if the field is missing,
        // so it's also backward-compatible.
        display_currency: showingUsd ? "USD" : "INR",
      });
      const isUsd = order.currency === "USD";
      const symbol = isUsd ? "$" : "₹";
      // Track whether the /verify handshake completed successfully. If
      // the modal is dismissed WITHOUT verify firing (e.g. buyer's PayPal
      // email differs from the AstroKuberChat account email, so
      // Razorpay's handler callback never fires), we silently attempt a
      // reconcile after a short delay so the wallet still gets credited
      // if Razorpay confirms a captured payment on the order.
      let verifyDone = false;
      const options = {
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        order_id: order.order_id,
        name: "AstroKuber",
        // Show description in the customer's currency so the Razorpay
        // popup matches what they saw on the AstroKuber screen.
        description: order.bonus > 0
          ? `Wallet top-up · ${symbol}${order.amount} + ₹${order.bonus} bonus credit`
          : `Wallet top-up · ${symbol}${order.amount}`,
        image: "https://kuber-astro.preview.emergentagent.com/favicon.ico",
        prefill: order.prefill,
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            const { data: result } = await api.post("/wallet/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              ad_session_id: getAdSessionId(),
              save_phone: !alreadyHasPhone && savePhoneForNext,
            });
            verifyDone = true;
            await checkAuth?.();
            if (result.already_credited) {
              toast.success(`Payment already credited. Wallet: ${formatPrice(result.balance)}`);
            } else {
              // Compose toast in the customer's display currency.
              const parts = [formatPrice(result.amount)];
              if (result.bonus > 0) parts.push(`${formatPrice(result.bonus)} pack bonus`);
              if (result.coupon_bonus > 0) parts.push(`${formatPrice(result.coupon_bonus)} (${result.coupon_code})`);
              if (result.cashback_bonus > 0) parts.push(`${formatPrice(result.cashback_bonus)} cashback 🎁`);
              toast.success(`Wallet topped up: ${parts.join(" + ")} = ${formatPrice(result.credited)}. Balance: ${formatPrice(result.balance)}`);

              // Google Ads / GA4 conversion — fires exactly once per real
              // purchase (skipped for `already_credited` so a re-verify
              // never double-counts). The event name `conversion_event_purchase`
              // is the GA4 conversion event Google Ads is importing — match
              // it exactly so Google Ads attributes value + transaction_id.
              if (typeof window !== "undefined" && typeof window.gtag === "function") {
                const chargeCurrency = result.charge_currency || "INR";
                const chargeValue = Number(result.charge_amount) || Number(result.amount) || 0;
                window.gtag("event", "conversion_event_purchase", {
                  value: chargeValue,
                  currency: chargeCurrency,
                  transaction_id: result.order_id || resp.razorpay_order_id,
                  new_customer: !!result.is_new_customer,
                });
              }
            }
            onSuccess?.();
            onClose?.();
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment verification failed. Contact support.");
          } finally {
            setPaying(false);
          }
        },
        modal: {
          // If the popup dismissed without the handler completing (very
          // common for PayPal payments made with an email different from
          // the AstroKuberChat account), try a silent reconcile after a
          // short delay to give Razorpay time to receive the async
          // capture webhook. Idempotent, so worst case it's a no-op.
          ondismiss: () => {
            setPaying(false);
            if (verifyDone) return;
            const orderIdToReconcile = order.order_id;
            setTimeout(async () => {
              try {
                const { data: rec } = await api.post("/wallet/razorpay/reconcile", {
                  razorpay_order_id: orderIdToReconcile,
                  ad_session_id: getAdSessionId(),
                });
                if (rec?.credited && !rec?.already_credited) {
                  await checkAuth?.();
                  toast.success(`Payment recovered! Wallet: ${formatPrice(rec.balance)}`);
                  // Fire the same gtag conversion for consistency.
                  if (typeof window !== "undefined" && typeof window.gtag === "function") {
                    window.gtag("event", "conversion_event_purchase", {
                      value: Number(rec.charge_amount) || Number(rec.amount) || 0,
                      currency: rec.charge_currency || "INR",
                      transaction_id: rec.order_id || orderIdToReconcile,
                      new_customer: !!rec.is_new_customer,
                    });
                  }
                  onSuccess?.();
                  onClose?.();
                }
              } catch (_err) {
                // Silent — user hasn't paid yet, so 404/409 is expected.
              }
            }, 4000);
          },
        },
      };
      let rzp;
      try {
        rzp = createRazorpayCheckout(options);
      } catch (ctorErr) {
        console.error("[WalletTopUp] Razorpay constructor failed:", ctorErr);
        toast.error(`Could not open Razorpay: ${ctorErr?.message || String(ctorErr)}`);
        setPaying(false);
        return;
      }
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed. Please try again.");
        setPaying(false);
      });
      try {
        rzp.open();
      } catch (openErr) {
        console.error("[WalletTopUp] rzp.open() threw:", openErr);
        toast.error(`Could not open payment popup: ${openErr?.message || String(openErr)}`);
        setPaying(false);
      }
    } catch (err) {
      // Surface the underlying cause so we can actually diagnose
      // production failures instead of a meaningless toast. Network /
      // HTTP errors come through axios with .response.data.detail;
      // anything else (constructor throws, generic Errors) we log + show.
      console.error("[WalletTopUp] handlePay failed:", err);
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        (typeof err === "string" ? err : null) ||
        "Could not start payment";
      const httpCode = err?.response?.status;
      toast.error(httpCode ? `${detail} (HTTP ${httpCode})` : detail);
      setPaying(false);
    }
  };

  if (!open) return null;

  // iOS Reader-mode: no in-app payment UI allowed. Instead of the full
  // top-up sheet, show a compliant "top up on the web" message that
  // links out to Safari.
  if (hidePurchases) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="topup-modal">
        <div className="card-surface rounded-md max-w-md w-full p-7 relative">
          <button onClick={onClose} className="absolute top-4 right-4 text-muted hover:text-gold transition-colors" data-testid="topup-close-btn">
            <X size={18} />
          </button>
          <div className="flex items-center gap-2 text-gold label-eyebrow">
            <Sparkle size={14} weight="fill" /> Wallet
          </div>
          <h3 className="font-serif-display text-2xl mt-2 font-light">Top up your wallet on the web</h3>
          <p className="text-muted text-sm mt-3 font-light">
            To keep the AstroKuberChat iOS app light and private, wallet
            top-ups happen on the website. Once you top up there, your
            balance appears here automatically the moment you refresh.
          </p>
          <p className="text-muted text-xs mt-3 font-light">
            Current balance <span className="text-[#3F2D1B] font-semibold">{formatPrice(Number(user?.wallet_balance || 0))}</span>.
          </p>
          <button
            onClick={() => {
              try { window.open("https://astrokuberchat.com/wallet?utm_source=ios_app", "_blank"); } catch { /* ignore */ }
              onClose?.();
            }}
            className="btn-primary rounded-full w-full py-3 mt-6 text-sm font-medium"
            data-testid="topup-open-web-btn"
          >
            Open astrokuberchat.com
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="topup-modal">
      <div className="card-surface rounded-md max-w-md w-full p-7 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} disabled={paying} className="absolute top-4 right-4 text-muted hover:text-gold disabled:opacity-40 transition-colors" data-testid="topup-close-btn">
          <X size={18} />
        </button>
        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Sparkle size={14} weight="fill" /> {headline ? "Add to wallet" : "Top up your wallet"}
        </div>
        <h3 className="font-serif-display text-2xl mt-2 font-light">{headline || "Continue your reading"}</h3>
        <p className="text-muted text-sm mt-2 font-light">
          {subline || (
            <>Current balance <span className="text-[#3F2D1B] font-semibold">{formatPrice(Number(user?.wallet_balance || 0))}</span>. Secure payment via Razorpay — UPI, cards, netbanking & wallets accepted.{showingUsd && <span className="text-[10px] block mt-1">Prices shown in USD are approximate. Charge is in INR; your card/PayPal handles the FX automatically.</span>}</>
          )}
        </p>

        <div className="grid grid-cols-2 gap-3 mt-5">
          {packs.map((p) => {
            const isSel = !customAmount && selected?.id === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => { setSelected(p); setCustomAmount(""); }}
                className={`relative rounded-md px-3 py-3 text-left transition-all border ${
                  isSel
                    ? "border-gold bg-[#FFF6E0]"
                    : "border-soft bg-white hover:border-gold/40"
                }`}
                data-testid={`topup-pack-${p.id}`}
              >
                {isSel && <CheckCircle size={14} weight="fill" className="absolute top-2 right-2 text-gold" />}
                <div className="font-mono text-base font-semibold text-[#3F2D1B]">{formatPrice(p.amount)}</div>
                {p.bonus > 0 ? (
                  <div className="text-[10px] text-emerald-700 font-medium mt-0.5">+ {formatPrice(p.bonus)} bonus</div>
                ) : (
                  <div className="text-[10px] text-muted mt-0.5">Starter pack</div>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-5">
          <label className="label-eyebrow">Or enter a custom amount</label>
          <div className="relative mt-2">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted text-sm">{currencySymbol}</span>
            <input
              type="number"
              min={showingUsd ? 0.5 : min}
              max={showingUsd ? Math.ceil(max / (usd_to_inr || 1)) : max}
              step={showingUsd ? "0.01" : "1"}
              value={customAmount}
              onChange={(e) => { setCustomAmount(e.target.value); setSelected(null); }}
              placeholder={`${displayMin} - ${displayMax}`}
              className="w-full pl-7 pr-3 py-2.5 border border-soft rounded-md focus:border-gold outline-none text-sm font-mono"
              data-testid="topup-custom-input"
            />
          </div>
          {showingUsd && customAmount && (
            <p className="mt-1.5 text-[11px] text-muted">
              ≈ ₹{Math.round((parseFloat(customAmount) || 0) * (usd_to_inr || 1))} wallet credit · charged in USD by Razorpay (PayPal supported)
            </p>
          )}
        </div>

        {/* Coupon code */}
        <div className="mt-4">
          <label className="label-eyebrow">Have a coupon code?</label>
          {appliedCoupon ? (
            <div className="mt-2 flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border border-emerald-300 bg-emerald-50" data-testid="coupon-applied">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <CheckCircle size={14} weight="fill" className="text-emerald-700 flex-shrink-0" />
                <span className="text-sm font-semibold text-emerald-800">{appliedCoupon.code}</span>
                <span className="text-xs text-emerald-700 truncate">
                  {appliedCoupon.code === "ANNIVERSARY50"
                    ? `50% cashback bonus · + ${formatPrice(appliedCoupon.bonus)}`
                    : `+ ${formatPrice(appliedCoupon.bonus)} bonus`}
                </span>
              </div>
              <button onClick={removeCoupon} className="text-xs text-emerald-800 hover:underline flex-shrink-0" data-testid="coupon-remove">Remove</button>
            </div>
          ) : (
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={couponInput}
                onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(null); }}
                placeholder="e.g. SHIVA"
                maxLength={20}
                className="flex-1 px-3 py-2.5 border border-soft rounded-md focus:border-gold outline-none text-sm font-mono uppercase tracking-wider"
                data-testid="coupon-input"
              />
              <button
                type="button"
                onClick={applyCoupon}
                disabled={couponChecking || !couponInput.trim()}
                className="btn-ghost rounded-md px-4 py-2.5 text-xs font-medium disabled:opacity-50"
                data-testid="coupon-apply-btn"
              >
                {couponChecking ? "..." : "Apply"}
              </button>
            </div>
          )}
          {couponError && (
            <p className="text-xs text-rose-700 mt-1.5" data-testid="coupon-error">{couponError}</p>
          )}
        </div>

        {/* Summary */}
        {isValid && (
          <div className="mt-5 p-3 rounded-md bg-[#FFF6E0]/60 border border-amber-200 text-xs space-y-1" data-testid="topup-summary">
            <div className="flex justify-between"><span className="text-muted">Recharge amount</span><span className="font-mono font-semibold text-[#3F2D1B]" data-testid="topup-subtotal">{formatPrice(effectiveAmount)}</span></div>
            <div className="flex justify-between" data-testid="topup-gst-row">
              <span className="text-muted">{taxLabel}</span>
              <span className="font-mono text-[#3F2D1B]" data-testid="topup-gst-amount">{formatPrice(gstAmount)}</span>
            </div>
            <div className="flex justify-between pt-1 border-t border-amber-200 mt-1 font-semibold">
              <span className="text-[#3F2D1B]">Total amount to pay</span>
              <span className="font-mono text-[#3F2D1B]" data-testid="topup-total-to-pay">{formatPrice(totalToPay)}</span>
            </div>
            {(selectedPackBonus > 0 || appliedCoupon || cashbackBonusPreview > 0) && (
              <div className="pt-2 mt-1 border-t border-amber-200/70 space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-amber-900/70">Wallet credit breakdown</div>
                <div className="flex justify-between"><span className="text-muted">Recharge credited</span><span className="font-mono">{formatPrice(effectiveAmount)}</span></div>
                {selectedPackBonus > 0 && (
                  <div className="flex justify-between text-emerald-700"><span>+ Pack bonus</span><span className="font-mono">{formatPrice(selectedPackBonus)}</span></div>
                )}
                {appliedCoupon && (
                  <div className="flex justify-between text-emerald-700"><span>+ {appliedCoupon.code} coupon</span><span className="font-mono">{formatPrice(appliedCoupon.bonus)}</span></div>
                )}
                {cashbackBonusPreview > 0 && (
                  <div className="flex justify-between text-amber-700 font-semibold" data-testid="cashback-bonus-row">
                    <span className="flex items-center gap-1">🎁 First-chat cashback</span>
                    <span className="font-mono">+ ₹{cashbackBonusPreview}</span>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-between pt-1 border-t border-amber-200 mt-1 font-semibold">
              <span className="text-gold">Total wallet credit</span>
              <span className="font-mono text-[#3F2D1B]">{formatPrice(totalCredit)}</span>
            </div>
            {showingUsd && (
              <div className="pt-1.5 text-[10px] text-amber-900/70 italic">
                Charged in USD by Razorpay (incl. 18% taxes). PayPal accepted for USD cards.
              </div>
            )}
          </div>
        )}

        {/* EMI availability hint — INR only. Nudges seekers below the
            ₹3,000 EMI threshold to bump their recharge so the EMI /
            Cardless-EMI / PayLater options appear inside Razorpay's
            checkout. Silent (and satisfied) once the amount clears
            the threshold so we don't over-communicate. */}
        {!showingUsd && (
          effectiveAmount > 0 && effectiveAmount < 3000 ? (
            <div
              className="mt-3 flex items-start gap-2 rounded-xl bg-gradient-to-r from-amber-50 to-amber-100/60 border border-amber-200/70 px-3 py-2"
              data-testid="topup-emi-hint"
            >
              <span className="text-base leading-none pt-[1px]" aria-hidden>💳</span>
              <div className="text-[11px] leading-snug text-amber-900">
                Recharge <b>₹3,000 or more</b> to unlock <b>No-cost EMI</b>,
                <b> Cardless EMI</b> and <b>Pay Later</b> at checkout.
                <button
                  type="button"
                  onClick={() => { setSelected(null); setCustomAmount("3000"); }}
                  className="ml-1 font-semibold underline decoration-dotted hover:text-amber-800 focus:outline-none"
                  data-testid="topup-emi-hint-set-3000"
                >
                  Set ₹3,000 →
                </button>
              </div>
            </div>
          ) : effectiveAmount >= 3000 ? (
            <div
              className="mt-3 flex items-center gap-2 text-[11px] text-emerald-700"
              data-testid="topup-emi-eligible"
            >
              <span aria-hidden>✓</span>
              EMI, Cardless EMI &amp; Pay Later will be available at checkout.
            </div>
          ) : null
        )}

        {!alreadyHasPhone && (
          <label
            className="mt-4 flex items-center gap-2 text-xs text-muted cursor-pointer select-none"
            data-testid="topup-save-phone-toggle"
          >
            <input
              type="checkbox"
              checked={savePhoneForNext}
              onChange={(e) => setSavePhoneForNext(e.target.checked)}
              className="w-4 h-4 accent-amber-500 cursor-pointer"
              data-testid="topup-save-phone-checkbox"
            />
            <span>Save phone number for faster next recharge</span>
          </label>
        )}

        <button
          onClick={handlePay}
          disabled={!isValid || paying}
          className="btn-primary w-full mt-6 rounded-full px-5 py-3 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="topup-pay-btn"
        >
          {paying ? (<><Spinner size={14} className="animate-spin" /> Processing...</>) : `Pay ${formatPrice(totalToPay || 0)} securely`}
        </button>

        {/* Native PayPal button — international (USD) customers ONLY.
            Rendered BELOW the primary Razorpay CTA so it appears as a
            true alternate rail (Razorpay = cards, PayPal = wallet).
            When Razorpay's mobile UI compresses PayPal into a hidden
            accordion, this button is the buyer's escape hatch — a
            full-width PayPal button they can't miss. INR customers
            never see this. */}
        {showingUsd && (
          <>
            <div className="flex items-center gap-3 my-3">
              <div className="flex-1 h-px bg-soft" />
              <span className="text-[10px] uppercase tracking-widest text-muted">or</span>
              <div className="flex-1 h-px bg-soft" />
            </div>
            <PayPalTopUpButton
              amount={effectiveAmount}
              couponCode={appliedCoupon?.code || null}
              disabled={!isValid || paying}
              onSuccess={async () => {
                await checkAuth?.();
                onSuccess?.();
                onClose?.();
              }}
              onError={() => setPaying(false)}
            />
          </>
        )}
        <p className="text-[10px] text-muted text-center mt-3">
          Payments are processed by Razorpay{showingUsd ? " or PayPal" : ""}. AstroKuber never sees your card details.
        </p>
      </div>
    </div>
  );
}
